import { CaretDown, CircleNotch } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
} from "react";

import { getWhitelabelCustomization } from "common/helpers/utils";

const filterButtonVariants = cva(
  [
    "border bg-white border-black-200 rounded-full items-center flex group text-black-950 transition-[background] duration-300 ease-in-out",
    "hover:bg-black-100",
    "disabled:bg-black-100 disabled:border-black-100 disabled:text-black-400 disabled:cursor-not-allowed",
    "focus-visible:border-purple-500 focus-visible:outline-none",
  ],
  {
    variants: {
      size: {
        sm: "h-8 gap-1 px-3 text-button-xs [&_svg]:size-4 [&>svg]:first:-ml-1",
        md: "h-10 gap-1.5 px-5 text-button-sm [&_svg]:size-5 [&>svg]:first:-ml-2.5",
        lg: "h-11 gap-2 px-6 text-button-md [&_svg]:size-5 [&>svg]:first:-ml-3",
      },
      hasWhitelabelColor: {
        true: "data-[state=open]:border-whitelabel-primary",
        false: "data-[state=open]:border-purple-500",
      },
    },
  },
);
const rightIconContainerClassNames = {
  sm: "gap-1 -mr-1",
  md: "gap-1.5 -mr-2.5",
  lg: "gap-2 -mr-3",
} as const;

interface FilterButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof filterButtonVariants> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
}

function FilterButton(
  {
    children,
    className,
    leftIcon,
    rightIcon,
    size = "md",
    isLoading,
    disabled,
    ...rest
  }: FilterButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      {...rest}
      type="button"
      ref={ref}
      className={filterButtonVariants({
        size,
        hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
        className,
      })}
      disabled={isLoading || disabled}
    >
      {isLoading ? (
        <CircleNotch className="animate-spin fill-black-400" />
      ) : (
        leftIcon
      )}
      <span className="flex-1 truncate text-left">{children}</span>
      <div
        className={clsx(
          "flex items-center",
          rightIconContainerClassNames[size],
        )}
      >
        {rightIcon}
        <CaretDown className="ml-auto transition-transform group-data-[state=open]:rotate-180" />
      </div>
    </button>
  );
}

export default forwardRef(FilterButton);
